import { create } from 'zustand';
import { adminApi } from '../services/adminApi';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: 'super_admin' | 'admin';
}

interface AdminAuthState {
  user: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

export const useAdminAuth = create<AdminAuthState>((set) => ({
  user: null,
  token: localStorage.getItem('admin_access_token'),
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const response = await adminApi.login(email, password);
      
      // Store token
      localStorage.setItem('admin_access_token', response.access_token);
      
      // Store user data
      set({
        user: response.user,
        token: response.access_token,
        isAuthenticated: true,
        isLoading: false,
      });
      
      console.log('[ADMIN AUTH] Login successful:', response.user.email);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      set({
        error: errorMessage,
        isLoading: false,
        isAuthenticated: false,
      });
      console.error('[ADMIN AUTH] Login failed:', errorMessage);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('admin_access_token');
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });
    console.log('[ADMIN AUTH] Logged out');
  },

  checkAuth: async () => {
    const token = localStorage.getItem('admin_access_token');
    
    if (!token) {
      set({ isAuthenticated: false });
      return;
    }
    
    try {
      const user = await adminApi.getCurrentUser();
      set({
        user,
        token,
        isAuthenticated: true,
      });
      console.log('[ADMIN AUTH] Auth check successful');
    } catch (error) {
      localStorage.removeItem('admin_access_token');
      set({
        user: null,
        token: null,
        isAuthenticated: false,
      });
      console.error('[ADMIN AUTH] Auth check failed');
    }
  },

  clearError: () => set({ error: null }),
}));
